import { ProfitType } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  AppliedSellRateRule,
  BuyRateRule,
  PricingFormula,
  SellRateData,
  SellRateFlatAmountUnit,
  SellRateFuelMode,
  SellRatePricingMode,
  SellRateRoundingRules,
  StopSummary,
} from 'types/agents/QuickQuoteAgent';
import { Quoting, integrationNameMap } from 'types/enums/Integrations';

import { formatBuyRateBandDollars, formatDollarsNoCents } from '../utils';
import { PCMilerDistanceSource } from '../utils/mileageSource';

const FALLBACK_LABEL = '—';

const quotingSourceValues = new Set<string>(Object.values(Quoting));
const FIXED_FINAL_LABEL = 'fixed final';

/** Accepts full SellRateData or partial margin fields (e.g. from LaneTooltipProps). */
type MarginDisplayProps = {
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  /** When margin is a flat amount, `TripTotal` is a one-time charge and `PerMile` scales by distance. */
  flatAmountUnit?: SellRateFlatAmountUnit;
};

type FixedFinalPricingProps = {
  pricingMode?: SellRatePricingMode;
  appliedSellRateRule?: AppliedSellRateRule;
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  sellRate?: number;
  buyRate?: number;
};

/**
 * Formats margin for display: $X or $X/mi for flat amounts, X.X% for percentages.
 * Shared by SingleLaneDescription, BatchLaneDescription, and applied sell-rate rules.
 */
export const getMarginDisplay = (
  data?: Maybe<MarginDisplayProps | SellRateData>
): Maybe<string> => {
  if (!data) {
    return null;
  }

  const margin = data as MarginDisplayProps;
  const marginLabel = margin.pricingFormula ?? PricingFormula.Markup;
  const isFlatAmount = margin.marginType === ProfitType.Amount;
  const safeMarginValue = margin.marginValue ?? 0;

  if (isFlatAmount) {
    if (margin.flatAmountUnit === SellRateFlatAmountUnit.PerMile) {
      return `$${safeMarginValue.toFixed(2)}/mi ${marginLabel}`;
    }
    return `$${safeMarginValue.toFixed(0)} ${marginLabel}`;
  }

  return `${safeMarginValue.toFixed(1)}% ${marginLabel}`;
};

export const isFixedFinalPricing = (
  data?: Maybe<FixedFinalPricingProps>
): boolean => {
  if (!data) {
    return false;
  }

  const pricingMode = data.pricingMode?.toString();
  const rulePricingMode = data.appliedSellRateRule?.pricingMode?.toString();

  if (
    pricingMode === SellRatePricingMode.FixedFinal ||
    rulePricingMode === SellRatePricingMode.FixedFinal
  ) {
    return true;
  }

  if (pricingMode != null || rulePricingMode != null) {
    return false;
  }

  const rule = data.appliedSellRateRule;
  if (!rule) {
    return false;
  }

  const distanceMiles = rule.distanceMiles;
  const perMileAmount = rule.marginValue;
  const rateAfterPricing = rule.rateAfterPricing ?? rule.rateAfterFuel;
  const expectedFixedFinal =
    distanceMiles != null && perMileAmount != null
      ? distanceMiles * perMileAmount
      : undefined;
  const sellRate = data.sellRate ?? rateAfterPricing;
  const hasZeroBuyRate =
    data.buyRate == null || data.buyRate === 0 || rule.rateBeforeRule === 0;

  return (
    rule.flatAmountUnit === SellRateFlatAmountUnit.PerMile &&
    rule.fuelMode === SellRateFuelMode.None &&
    hasZeroBuyRate &&
    expectedFixedFinal != null &&
    rateAfterPricing != null &&
    sellRate != null &&
    Math.abs(expectedFixedFinal - rateAfterPricing) < 1 &&
    Math.abs(rateAfterPricing - sellRate) < 1
  );
};

export const getFixedFinalPricingMathLabel = (
  data?: Maybe<FixedFinalPricingProps>
): Maybe<string> => {
  if (!data) {
    return null;
  }

  const rule = data.appliedSellRateRule;
  const marginType = rule?.marginType ?? data.marginType;
  const marginValue = rule?.marginValue ?? data.marginValue;
  const flatAmountUnit = rule?.flatAmountUnit;
  const distanceMiles = rule?.distanceMiles;

  if (marginValue == null) {
    return null;
  }

  if (marginType === ProfitType.Amount) {
    if (flatAmountUnit === SellRateFlatAmountUnit.PerMile) {
      const miles =
        distanceMiles != null && distanceMiles > 0
          ? ` x ${formatDistanceMiles(distanceMiles)} mi`
          : '';

      return `$${marginValue.toFixed(2)}/mi${miles}`;
    }

    return `$${marginValue.toFixed(0)} ${FIXED_FINAL_LABEL}`;
  }

  return `${marginValue.toFixed(1)}% ${FIXED_FINAL_LABEL}`;
};

export const formatDistanceMiles = (distanceMiles: number): string => {
  if (!Number.isFinite(distanceMiles)) {
    return '';
  }

  return distanceMiles.toFixed(2).replace(/\.?0+$/, '');
};

export const getFixedFinalRuleDisplay = (
  data?: Maybe<FixedFinalPricingProps>
): string => {
  const ruleName = data?.appliedSellRateRule?.ruleName;
  const mathLabel = getFixedFinalPricingMathLabel(data);
  const ruleLabel = ruleName
    ? `sell rate rule: "${ruleName}"`
    : 'sell rate rule';

  return mathLabel ? `${ruleLabel} (${mathLabel})` : ruleLabel;
};

export const getDistanceSourceDisplay = (distanceSource?: string): string => {
  const normalized = distanceSource?.trim().toLowerCase();

  if (normalized === 'redis') {
    return 'Redis';
  }
  if (normalized === 'dat') {
    return 'DAT mileage';
  }
  if (normalized === PCMilerDistanceSource) {
    return 'PC*Miler';
  }

  return '';
};

export const getTransportTypeDisplay = (transportType: unknown): string => {
  if (typeof transportType === 'string' && transportType.length > 0) {
    return transportType.toLowerCase();
  }
  return FALLBACK_LABEL;
};

export const getQuoteSourceDisplay = (selectedQuoteSource: unknown): string => {
  if (
    typeof selectedQuoteSource !== 'string' ||
    selectedQuoteSource.length === 0
  ) {
    return FALLBACK_LABEL;
  }

  const normalized = selectedQuoteSource.toLowerCase();
  if (quotingSourceValues.has(normalized)) {
    return integrationNameMap[normalized as Quoting];
  }

  return selectedQuoteSource.toUpperCase();
};

export const formatStopLocationShort = (stop?: StopSummary): string => {
  if (!stop) {
    return '';
  }

  const city = stop.city?.trim();
  const state = stop.state?.trim();
  const zip = stop.zip?.trim();

  const cityState = [city, state].filter(Boolean).join(', ');
  if (cityState) {
    return cityState;
  }

  if (zip) {
    return zip;
  }

  return '';
};

export const formatLaneRoute = (stops?: StopSummary[]): string => {
  if (!stops || stops.length < 2) {
    return '';
  }

  const routeParts: string[] = [];
  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];
    const formatted = formatStopLocationShort(stop);
    if (formatted) {
      routeParts.push(formatted);
      continue;
    }

    const fallbackLabel =
      stop != null ? `Stop ${stop.stopNumber ?? i + 1}` : `Stop ${i + 1}`;
    routeParts.push(fallbackLabel);
  }

  return routeParts.join(' → ');
};

export const formatLaneRouteShort = (stops?: StopSummary[]): string => {
  if (!stops || stops.length < 2) {
    return '';
  }

  if (stops.length <= 2) {
    return formatLaneRoute(stops);
  }

  const firstStop = formatStopLocationShort(stops[0]) || 'Stop 1';
  const lastStop =
    formatStopLocationShort(stops[stops.length - 1]) || `Stop ${stops.length}`;
  const middleStopsCount = stops.length - 2;

  return `${firstStop} +${middleStopsCount} → ${lastStop}`;
};

/** Single source of truth: use rounded sell rate when present and non-null, else fall back to sell rate. */
export const getDisplaySellValue = (
  sellRateRounded: MaybeUndef<number>,
  sellRate: MaybeUndef<number>
): MaybeUndef<number> =>
  sellRateRounded !== undefined && sellRateRounded !== null
    ? sellRateRounded
    : sellRate;

export interface LaneTooltipProps {
  buyRate?: number;
  originalBuyRate?: number;
  buyRateRules?: BuyRateRule[];
  buyRateRangeLow?: number;
  buyRateRangeHigh?: number;
  transportType?: string;
  selectedQuoteSource?: string;
  sellRate?: number;
  sellRateRounded?: number;
  roundingRules?: SellRateRoundingRules;
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  pricingMode?: SellRatePricingMode;
  appliedSellRateRule?: AppliedSellRateRule;
  distanceSource?: string;
}

export interface BuyRateBreakdownState {
  hasBuyRateBreakdown: boolean;
  showStandaloneQuoteRange: boolean;
  showMetadataTargetBuyInRange: boolean;
  showFinalBuyRateRow: boolean;
}

export const getLaneTooltipContent = (props: LaneTooltipProps): string => {
  const {
    buyRate,
    transportType,
    selectedQuoteSource,
    sellRate,
    sellRateRounded,
    roundingRules,
    marginType,
    marginValue,
    pricingFormula,
    buyRateRules = [],
    originalBuyRate,
    buyRateRangeLow,
    buyRateRangeHigh,
    appliedSellRateRule,
    pricingMode,
    distanceSource,
  } = props;
  const buyFormatted = formatDollarsNoCents(buyRate);
  const transportTypeLowercase = transportType?.toLowerCase() || 'transport';
  const selectedQuoteSourceUppercase =
    getQuoteSourceDisplay(selectedQuoteSource);

  const sourceFragment =
    selectedQuoteSourceUppercase && selectedQuoteSourceUppercase !== '—'
      ? ` from ${selectedQuoteSourceUppercase}`
      : '';

  const sellValue = getDisplaySellValue(sellRateRounded, sellRate);

  const hasSell = sellValue !== undefined && sellValue !== null;

  const isFixedFinal = isFixedFinalPricing({
    pricingMode,
    appliedSellRateRule,
    marginType,
    marginValue,
    pricingFormula,
    sellRate,
    buyRate,
  });

  if (isFixedFinal && hasSell) {
    const sourceDisplay = getDistanceSourceDisplay(distanceSource);
    const distanceFragment = sourceDisplay
      ? ` using ${sourceDisplay} distance`
      : '';

    return `Drumkit applied fixed final ${getFixedFinalRuleDisplay({
      appliedSellRateRule,
      marginType,
      marginValue,
      pricingFormula,
    })}${distanceFragment} to calculate a final quote of ${formatDollarsNoCents(sellValue)}.`;
  }

  let buyRateText = `Drumkit found a ${buyFormatted} ${transportTypeLowercase} quote${sourceFragment}.`;
  if (buyRateRules.length > 0 && originalBuyRate != null) {
    buyRateText = `Drumkit found a ${formatDollarsNoCents(originalBuyRate)} ${transportTypeLowercase} quote${sourceFragment}; buy rate rules adjusted the final buy rate to ${buyFormatted}.`;
  } else if (buyRateRules.length > 0) {
    buyRateText = `Drumkit applied buy rate rules to reach a final buy rate of ${buyFormatted}.`;
  }

  if (buyRateRangeLow != null && buyRateRangeHigh != null) {
    buyRateText += ` The quote range was ${formatBuyRateBandDollars(buyRateRangeLow)}–${formatBuyRateBandDollars(buyRateRangeHigh)}.`;
  }

  if (!hasSell) {
    return `${buyRateText} No margin was applied.`;
  }

  const ruleAwarePricingDisplay = appliedSellRateRule
    ? (getMarginDisplay(appliedSellRateRule) ?? '')
    : (getMarginDisplay({ marginType, marginValue, pricingFormula }) ?? '');

  const sellFormatted = formatDollarsNoCents(sellValue);
  const sellRulePrefix = appliedSellRateRule
    ? `Sell rate rule ${appliedSellRateRule.ruleName ? `"${appliedSellRateRule.ruleName}"` : 'override'} applied ${ruleAwarePricingDisplay}`
    : `Applied the agent's default ${ruleAwarePricingDisplay}`;

  let tooltipText = `${buyRateText} ${sellRulePrefix} to calculate a sell rate of ${sellFormatted}.`;

  if (roundingRules?.roundToNearest) {
    const roundingDirection = roundingRules.roundUp ? 'up' : 'down';
    tooltipText += ` The sell rate was rounded ${roundingDirection} to the nearest $${roundingRules.roundToNearest.toFixed(0)}.`;
  }

  return tooltipText;
};

export const getBuyRateBreakdownState = (
  buyRateRules: BuyRateRule[],
  buyRateRangeLow: Undef<number>,
  buyRateRangeHigh: Undef<number>
): BuyRateBreakdownState => {
  const hasRangePercentileRule = buyRateRules.some(
    (rule) => rule.adjustmentType === 'rangePercentile'
  );
  const hasBuyRateBand = buyRateRangeLow != null && buyRateRangeHigh != null;
  const showStandaloneQuoteRange = hasBuyRateBand && !hasRangePercentileRule;
  const showMetadataTargetBuyInRange =
    showStandaloneQuoteRange && buyRateRules.length === 0;

  return {
    hasBuyRateBreakdown: buyRateRules.length > 0 || hasBuyRateBand,
    showStandaloneQuoteRange,
    showMetadataTargetBuyInRange,
    showFinalBuyRateRow: !showMetadataTargetBuyInRange,
  };
};
